import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const conflictData = [
  { flag: '🇺🇦', contracts: '€3.2B', items: ['Leopard 2A6 — 100+', 'Marder 1A3 — 200+', 'Gepard — 50', '155mm — 500,000'], color: 'from-yellow-500/20 to-blue-500/20', borderColor: 'border-yellow-500/30', statusKey: 'c1_status' },
  { flag: '🇵🇱', contracts: '€12B',  items: ['Lynx KF41 — 500', 'K2 turrets — 800', 'PzH 2000 — 48', 'Ammo — €2B'], color: 'from-red-500/20 to-white/5', borderColor: 'border-red-500/30', statusKey: 'c2_status' },
  { flag: '🇩🇪', contracts: '€8.5B', items: ['Leopard 2A8 — 123', 'Lynx KF41 — 143', 'PzH 2000 — 18', 'Skyranger — 60'], color: 'from-gray-500/20 to-yellow-500/10', borderColor: 'border-gray-400/30', statusKey: 'c3_status' },
  { flag: '🇭🇺', contracts: '€2.1B', items: ['Lynx KF41 — 218', 'Local Rheinmetall factory', 'Tech transfer', 'Service center'], color: 'from-red-500/15 to-green-500/10', borderColor: 'border-red-400/30', statusKey: 'c4_status' },
  { flag: '🇦🇺', contracts: '€5.5B', items: ['Lynx KF41 — 450', 'Queensland factory', 'Boxer MRAV — 211', '30yr service'], color: 'from-yellow-500/15 to-red-500/10', borderColor: 'border-yellow-400/30', statusKey: 'c5_status' },
  { flag: '🇷🇴', contracts: '€3.8B', items: ['Lynx KF41 — 300', 'PzH 2000 — 54', 'Ammo — €500M', 'Training'], color: 'from-blue-500/15 to-yellow-500/10', borderColor: 'border-blue-400/30', statusKey: 'c6_status' },
];

const ConflictsSection = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const statusColorMap: Record<string, string> = {
    'Active': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Активно': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Активно': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Attivo': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Contract signed': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Контракт подписан': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Договорът е подписан': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Contratto firmato': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Long-term': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Долгосрочный': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Дългосрочен': 'bg-green-500/20 text-green-400 border-green-500/30',
    'A lungo termine': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Production': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Производство': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Produzione': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'New contract': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Новый контракт': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Нов договор': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Nuovo contratto': 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30',
    'Negotiations': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'Переговоры': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'Преговори': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'Negoziati': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <section id="conflicts" className="py-24 bg-[#0d0d0d] relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <img src="https://public.readdy.ai/ai/img_res/b49c2ab8d62cbc5eed16c66f275cae73.jpg" alt="" className="w-full h-full object-cover object-top" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('conflicts_badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('conflicts_title1')}<br />
            <span className="text-[#d4af37]">{t('conflicts_title2')}</span>
          </h2>
          <p className="text-white/55 text-base max-w-2xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('conflicts_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {conflictData.map((c, i) => {
            const status = t(`c${i+1}_status`);
            return (
              <div
                key={i}
                className={`bg-gradient-to-br ${c.color} border ${c.borderColor} rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => setExpanded(expanded === i ? null : i)}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{c.flag}</span>
                      <div>
                        <div className="text-white font-bold text-base" style={{ fontFamily: "'Oswald', sans-serif" }}>{t(`c${i+1}_region`)}</div>
                        <div className="text-[#d4af37] text-xs tracking-wider">{c.contracts}</div>
                      </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${statusColorMap[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                      {status}
                    </span>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {t(`c${i+1}_desc`)}
                  </p>
                  <div className={`overflow-hidden transition-all duration-300 ${expanded === i ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="border-t border-white/10 pt-3 mt-1">
                      <div className="text-[#d4af37] text-xs tracking-widest uppercase mb-2 font-medium">{t('conflicts_deliveries')}</div>
                      <div className="flex flex-col gap-1.5">
                        {c.items.map((item, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-[#d4af37] rounded-full flex-shrink-0"></div>
                            <span className="text-white/65 text-xs">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button className="flex items-center gap-1 text-[#d4af37] text-xs tracking-wider uppercase mt-3 cursor-pointer whitespace-nowrap">
                    <span>{expanded === i ? t('conflicts_hide') : t('conflicts_more')}</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${expanded === i ? 'ri-arrow-up-s-line' : 'ri-arrow-right-s-line'} text-sm`}></i>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={`bg-gradient-to-r from-[#d4af37]/10 to-transparent border border-[#d4af37]/20 rounded-2xl p-8 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="text-[#d4af37] text-xs tracking-widest uppercase mb-3 font-medium">{t('conflicts_why_label')}</div>
              <h3 className="text-2xl font-black text-white mb-4 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {t('conflicts_why_title')}
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}
                dangerouslySetInnerHTML={{ __html: t('conflicts_why_desc') }}
              />
            </div>
            <div className="grid grid-cols-3 gap-4 flex-shrink-0 w-full lg:w-auto">
              {[
                { value: '+280%', label: t('stat_stocks') },
                { value: '€50B',  label: t('stat_backlog') },
                { value: '8×',    label: t('stat_ammo') },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-xl md:text-3xl font-black text-[#d4af37] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>{stat.value}</div>
                  <div className="text-white/50 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConflictsSection;